import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { getAppInfo, getPlatform, isNative } from '@/lib/capacitor';
import { getDiagLog, clearDiagLog, type DiagEntry, type DiagSnapshot } from '@/lib/diag';
import { type DiagTap, isInteractiveTapTarget, trackDiagTap } from './diag-gesture';

/**
 * Diagnostics viewer for the #5105 native member-only login loop.
 *
 * Reachable two ways, because the loop renders in the gate chain BEFORE the app
 * shell — so the under-your-name menu is unreachable while stuck:
 *   1. Loop-proof: 5 rapid taps on ONE non-interactive spot (works on the bare
 *      loader/strobe, where there is nothing but non-interactive surface).
 *      Guarded (#5328, see diag-gesture.ts): taps on/inside real controls never
 *      count, and the taps must be stationary — so hammering a button (e.g.
 *      Brand Kit Randomize) or ordinary fast clicking can never open this.
 *   2. The `xavior:open-diag` window event (dispatched from ProfileModal via
 *      `openDiagPanel()`).
 *
 * Reads the reload-surviving `xavior:diag:*` buffer (see lib/diag.ts), so it
 * shows what happened across the strobe's reload cycles, plus a live session
 * snapshot. Copy / select-all so a tester can send it without DevTools.
 *
 * PRESENTATIONAL: the session snapshot is injected as a prop (built by the
 * composition-layer `app/DiagHost`), so `common/` never imports `features/auth`
 * (import-direction / ADR 024).
 */

const OPEN_EVENT = 'xavior:open-diag';

function formatEntries(entries: DiagEntry[]): string {
  return entries
    .map((e) => {
      // Defensive: a tampered/legacy buffer entry may lack a numeric `t`.
      const ts = Number.isFinite(e.t)
        ? new Date(e.t).toISOString().slice(11, 23)
        : '??:??:??';
      const data = e.data !== undefined ? ` ${JSON.stringify(e.data)}` : '';
      return `${ts} [${e.tag}] ${e.msg}${data}`;
    })
    .join('\n');
}

function DiagPanel({ snapshot, onClose }: { snapshot: DiagSnapshot; onClose: () => void }) {
  const [appInfo, setAppInfo] = useState<{ version: string; build: string } | null>(null);
  const [copied, setCopied] = useState(false);
  // Snapshot the buffer once on open so the list doesn't churn under the reader.
  const [entries, setEntries] = useState<DiagEntry[]>(() => getDiagLog());

  useEffect(() => {
    let alive = true;
    void getAppInfo().then((info) => {
      if (alive) setAppInfo(info);
    });
    return () => {
      alive = false;
    };
  }, []);

  const summary = useMemo(
    () => ({
      platform: getPlatform(),
      native: isNative(),
      build: appInfo ? `${appInfo.version} (${appInfo.build})` : 'web',
      ...snapshot,
    }),
    [appInfo, snapshot],
  );

  const text = useMemo(
    () => `# XAVIOR diagnostics\n${JSON.stringify(summary, null, 2)}\n\n# Log (oldest → newest)\n${formatEntries(entries)}`,
    [summary, entries],
  );

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // WKWebView clipboard can fail — fall back to manual select-all.
      textareaRef.current?.select();
    }
  }, [text]);

  const panel = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483647,
        background: 'rgba(0,0,0,0.82)',
        color: '#e6e6e6',
        font: '12px/1.45 ui-monospace, SFMono-Regular, Menlo, monospace',
        display: 'flex',
        flexDirection: 'column',
        padding: 'max(env(safe-area-inset-top), 12px) 12px 12px',
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Diagnostics"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <strong style={{ flex: 1, fontSize: 14 }}>Diagnostics</strong>
        <button type="button" onClick={handleCopy} style={btn}>
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button
          type="button"
          onClick={() => {
            clearDiagLog();
            setEntries([]);
          }}
          style={btn}
        >
          Clear
        </button>
        <button type="button" onClick={() => setEntries(getDiagLog())} style={btn}>
          Refresh
        </button>
        <button type="button" onClick={onClose} style={{ ...btn, background: '#444' }}>
          Close
        </button>
      </div>
      <textarea
        ref={textareaRef}
        readOnly
        value={text}
        style={{
          flex: 1,
          width: '100%',
          resize: 'none',
          background: '#111',
          color: '#d6f5d6',
          border: '1px solid #333',
          borderRadius: 8,
          padding: 10,
          font: 'inherit',
          whiteSpace: 'pre',
          overflow: 'auto',
        }}
      />
    </div>
  );

  return createPortal(panel, document.body);
}

const btn: CSSProperties = {
  padding: '6px 10px',
  borderRadius: 6,
  border: 'none',
  background: '#0EA5E9',
  color: '#fff',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};

/**
 * Always-mounted host: wires the loop-proof tap-gesture + open-event, and shows
 * the panel. Mount once at the app root via `app/DiagHost` (which injects the
 * session `snapshot`), as a sibling of the gate chain so it survives the gate
 * chain's early-return loaders.
 */
export function DiagOverlay({ snapshot }: { snapshot: DiagSnapshot }) {
  const [open, setOpen] = useState(false);
  const taps = useRef<DiagTap[]>([]);

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      // #5328: taps on real controls never count toward the gesture.
      if (isInteractiveTapTarget(e.target)) return;
      const next = trackDiagTap(taps.current, {
        t: Date.now(),
        x: e.clientX,
        y: e.clientY,
      });
      taps.current = next.taps;
      if (next.fire) setOpen(true);
    };
    const onOpen = () => setOpen(true);
    document.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener(OPEN_EVENT, onOpen);
    };
  }, []);

  if (!open) return null;
  return <DiagPanel snapshot={snapshot} onClose={() => setOpen(false)} />;
}

/** Dispatch from anywhere (e.g. ProfileModal) to open the panel. */
export function openDiagPanel(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(OPEN_EVENT));
}
